import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom"; // Added useNavigate

import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";

import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import Breadcrumbs from "examples/Breadcrumbs";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useMediaQuery } from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { TextField } from "@mui/material";
import { Drawer, Box } from "@mui/material";
import getFilteredRoutes from "utils/getFilteredRoutes";
import NotificationBell from "client/components/notifications/NotificationBell";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import { Select, FormControl, InputLabel } from "@mui/material";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;

  const [accountMenu, setAccountMenu] = useState(null); // for account_circle menu
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate(); // for programmatic navigation
  const isMobileOrTablet = useMediaQuery("(max-width:1200px)");
  const [openMenu, setOpenMenu] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [loadingColleges, setLoadingColleges] = useState(true);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedRole, setSelectedRole] = useState(
    sessionStorage.getItem("selected_role_id") || ""
  );
  const [openFilters, setOpenFilters] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const fetchUserColleges = async () => {
    try {
      const storedUser = JSON.parse(sessionStorage.getItem("user"));
      if (!storedUser) return;

      const { client_id: clientId, user_id: userId } = storedUser;

      const res = await fetch(
        `${API_BASE_URL}/api/rbacall/user-college-login/${clientId}/${userId}`
      );

      const data = await res.json();

      if (data.success) {
        const userColleges = data.data.map((c) => ({
          id: String(c.college_id),
          name: `${c.college_name}-${c.college_id}`,
        }));

        setColleges(userColleges);

        const savedCollegeId = sessionStorage.getItem("selected_college_id");

        const currentCollegeId = String(storedUser.college_id || "");

        const selected =
          userColleges.find((c) => c.id === savedCollegeId) ||
          userColleges.find((c) => c.id === currentCollegeId) ||
          userColleges[0];

        const collegeId = selected?.id || "";

        // Ensure dropdown selection matches the loaded colleges
        setSelectedCollege(collegeId);

        if (collegeId) {
          sessionStorage.setItem("selected_college_id", collegeId);

          sessionStorage.setItem(
            "user",
            JSON.stringify({
              ...storedUser,
              college_id: Number(collegeId),
            })
          );
        }
      }
    } catch (error) {
      console.error("Failed to load colleges:", error);
    } finally {
      setLoadingColleges(false);
    }
  };

  const fetchUserRoles = async (collegeId, autoSelectFirst = false) => {
    try {
      setLoadingRoles(true);

      const storedUser = JSON.parse(sessionStorage.getItem("user"));

      if (!storedUser?.user_id || !collegeId) {
        setRoles([]);
        return [];
      }

      const response = await fetch(
        `${API_BASE_URL}/api/rbacall/user-college-roles-login/${storedUser.user_id}/${collegeId}`
      );

      const data = await response.json();

      if (data.success) {
        const userRoles = data.data.roles || [];

        setRoles(userRoles);

        // Automatically select the first role after login
        if (autoSelectFirst && userRoles.length > 0) {
          const firstRole = userRoles[0];
          const firstRoleId = String(firstRole.role_id);

          setSelectedRole(firstRoleId);

          sessionStorage.setItem("selected_role_id", firstRoleId);

          // Update user object
          const updatedUser = {
            ...storedUser,
            college_id: Number(collegeId),
            role_id: Number(firstRole.role_id),
          };

          sessionStorage.setItem("user", JSON.stringify(updatedUser));

          // Load permissions for first role
          const permissionResponse = await fetch(
            `${API_BASE_URL}/api/rbacall/user-permissions/${firstRole.role_id}`
          );

          const permissionData = await permissionResponse.json();

          if (permissionData.success) {
            sessionStorage.setItem("permissions", JSON.stringify(permissionData.data));

            // Rebuild sidebar immediately
            const filteredRoutes = getFilteredRoutes();

            window.dispatchEvent(
              new CustomEvent("rbac-permissions-changed", {
                detail: {
                  routes: filteredRoutes,
                },
              })
            );
          } else {
            console.error("Failed to load permissions:", permissionData.message);
          }
        }

        return userRoles;
      } else {
        setRoles([]);
        return [];
      }
    } catch (error) {
      console.error("Failed to load roles:", error);
      setRoles([]);
      return [];
    } finally {
      setLoadingRoles(false);
    }
  };
  useEffect(() => {
    fetchUserColleges();
  }, []);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Notifications menu

  // Account menu
  const handleAccountMenuOpen = (event) => setAccountMenu(event.currentTarget);
  const handleAccountMenuClose = () => setAccountMenu(null);
  const handleLogout = () => {
    sessionStorage.removeItem("token");
    const landingPage = localStorage.getItem("landingPage");
    localStorage.removeItem("landingPage");

    if (!landingPage) navigate("/authentication/sign-in");
    else window.location.href = landingPage;
  };
  let storedUser = null;
  const userJson = sessionStorage.getItem("user");

  if (userJson) {
    try {
      storedUser = JSON.parse(userJson);
    } catch (e) {
      console.error("Failed to parse user from sessionStorage:", e);
      storedUser = null;
    }
  }
  const [selectedCollege, setSelectedCollege] = useState(storedUser?.college_id || "");

  const handleCollegeChange = async (event) => {
    const newCollegeId = event.target.value;
    if (!newCollegeId) {
      return;
    }
    const collegeId = Number(newCollegeId);
    setSelectedCollege(collegeId); /* * Update user */
    const storedUser = JSON.parse(sessionStorage.getItem("user"));

    if (storedUser) {
      const updatedUser = { ...storedUser, college_id: collegeId, role_id: null };
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
    }

    sessionStorage.setItem("selected_college_id", String(collegeId));

    setSelectedRole("");
    sessionStorage.removeItem("selected_role_id");
    sessionStorage.removeItem("permissions");
    /* * Load roles for this college. */
    await fetchUserRoles(
      collegeId
    ); /* * Rebuild sidebar. * * Since permissions were cleared, * only mandatory routes remain until * a role is selected. */
    const routes = await getFilteredRoutes();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("rbac-permissions-changed", { detail: { routes } }));
    }, 0);
    //navigate("/dashboard", { replace: true }); /* * Navigate to dashboard after college change. */
    //window.location.reload();
  };
  useEffect(() => {
    if (loadingColleges || !selectedCollege) return;

    const existingRole = sessionStorage.getItem("selected_role_id");

    fetchUserRoles(selectedCollege, !existingRole);
  }, [selectedCollege, loadingColleges]);

  const handleRoleChange = async (event) => {
    const roleId = event.target.value;

    if (!roleId) {
      return;
    }

    try {
      /*
       * Set selected role
       */
      setSelectedRole(roleId);

      sessionStorage.setItem("selected_role_id", roleId);

      /*
       * Update user object
       */
      const storedUser = JSON.parse(sessionStorage.getItem("user"));

      if (storedUser) {
        const updatedUser = {
          ...storedUser,
          role_id: Number(roleId),
        };

        sessionStorage.setItem("user", JSON.stringify(updatedUser));
      }

      const response = await fetch(`${API_BASE_URL}/api/rbacall/user-permissions/${roleId}`);

      const data = await response.json();

      if (!data.success) {
        console.error("Failed to load permissions:", data.message);

        return;
      }

      /*
       * Store complete permissions
       */
      sessionStorage.setItem("permissions", JSON.stringify(data.data));

      /*
       * Generate sidebar routes from the
       * newly stored permissions.
       */
      const filteredRoutes = getFilteredRoutes();

      /*
       * Notify App.js that RBAC permissions
       * have changed.
       */
      window.dispatchEvent(
        new CustomEvent("rbac-permissions-changed", {
          detail: {
            routes: filteredRoutes,
          },
        })
      );
    } catch (error) {
      console.error("Failed to change role:", error);
    }
  };

  const username = storedUser?.user_name || "Guest";
  const token = sessionStorage.getItem("token"); // Or however you store it
  let tokenEmail = "Guest";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tokenEmail = decoded.user_email || "Guest";
    } catch (error) {
      console.error("Failed to decode token:", error);
    }
  }

  const renderAccountMenu = () => (
    <Menu
      anchorEl={accountMenu}
      open={Boolean(accountMenu)}
      onClose={handleAccountMenuClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{ mt: 2 }}
    >
      <MenuItem>
        <Icon fontSize="small" sx={{ mr: 1 }}>
          person
        </Icon>
        College-{storedUser?.college_id} <br></br>
        {username} <br></br>
        {tokenEmail}
      </MenuItem>

      <MenuItem onClick={handleLogout}>
        <Icon fontSize="small" sx={{ mr: 1 }}>
          logout
        </Icon>
        Logout
      </MenuItem>
    </Menu>
  );
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => ({
        ...navbar(theme, {
          transparentNavbar,
          absolute,
          light,
          darkMode,
        }),
        color: "red",
        backgroundColor: "white",
        Index: theme.zIndex.drawer + 1,
      })}
    >
      {/* =========================================================
        TOP NAVBAR
       ========================================================= */}
      <Toolbar
        sx={{
          minHeight: {
            xs: "56px !important",
            sm: "60px !important",
            md: "64px !important",
          },

          px: {
            xs: 1,
            sm: 1.5,
            md: 2,
          },

          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          width: "100%",
        }}
      >
        {/* =====================================================
      DESKTOP / MOBILE LEFT SECTION
     ===================================================== */}
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",

            // Desktop: don't consume too much space
            flexShrink: 0,

            minWidth: 0,
          }}
        >
          {/* Desktop sidebar button */}
          {!isMobileOrTablet && (
            <IconButton
              onClick={() => setMiniSidenav(dispatch, !miniSidenav)}
              sx={{
                flexShrink: 0,
              }}
            >
              <Icon sx={iconsStyle} fontSize="medium">
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>
          )}

          {/* ROUTE NAME */}
          <MDBox
            color="inherit"
            sx={{
              minWidth: 0,

              // Desktop route width
              width: {
                xs: "auto",
                md: 140,
                lg: 170,
              },

              flexShrink: 0,
              ml: {
                xs: 0.5,
                md: 1,
              },
            }}
          >
            <h4
              style={{
                margin: 0,
                fontSize: "16px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {route.length > 0
                ? route[route.length - 1].charAt(0).toUpperCase() + route[route.length - 1].slice(1)
                : "Home"}
            </h4>
          </MDBox>
        </MDBox>

        {/* =====================================================
      DESKTOP: COLLEGE + ROLE + SEARCH
      ALL IN SAME ROW
     ===================================================== */}
        {!isMobileOrTablet && !isMini && (
          <MDBox
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",

              gap: 1,

              flex: 1,
              minWidth: 0,
            }}
          >
            {/* COLLEGE */}
            <MDBox
              sx={{
                width: {
                  md: 300,
                  lg: 420,
                  xl: 480,
                },

                flexShrink: 1,
                minWidth: 180,
              }}
            >
              <TextField
                label="College"
                name="college"
                select
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  native: true,
                }}
                value={selectedCollege}
                onChange={handleCollegeChange}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#64748B",
                    fontWeight: 600,
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#2563EB",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#F8FAFC",
                    color: "#1E293B",
                    "& fieldset": {
                      borderColor: "#CBD5E1",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2563EB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2563EB",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiNativeSelect-select": {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    paddingTop: "11px",
                    paddingBottom: "11px",
                  },
                }}
              >
                <option value="">Select College</option>

                {loadingColleges && <option value="">Loading...</option>}

                {!loadingColleges &&
                  colleges.map((college) => (
                    <option key={college.id} value={college.id}>
                      {college.name}
                    </option>
                  ))}
              </TextField>
            </MDBox>

            {/* ROLE */}
            <MDBox
              sx={{
                width: {
                  md: 180,
                  lg: 240,
                  xl: 300,
                },

                flexShrink: 1,
                minWidth: 140,
              }}
            >
              <TextField
                label="Role"
                name="role"
                select
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  native: true,
                }}
                value={selectedRole}
                onChange={handleRoleChange}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#64748B",
                    fontWeight: 600,
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#2563EB",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#F8FAFC",
                    color: "#1E293B",
                    "& fieldset": {
                      borderColor: "#CBD5E1",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2563EB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2563EB",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiNativeSelect-select": {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    paddingTop: "11px",
                    paddingBottom: "11px",
                  },
                }}
              >
                <option value="">Select Role</option>

                {loadingRoles && <option value="">Loading...</option>}

                {!loadingRoles &&
                  roles.map((role) => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </option>
                  ))}
              </TextField>
            </MDBox>

            {/* SEARCH */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 120,
              }}
            >
              <MDInput label="Search here" fullWidth />
            </MDBox>
          </MDBox>
        )}

        {/* =====================================================
      RIGHT SIDE
     ===================================================== */}
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            ml: "auto",
          }}
        >
          {/* MOBILE FILTER */}
          {isMobileOrTablet && (
            <IconButton
              size="small"
              color="inherit"
              onClick={() => setOpenFilters((prev) => !prev)}
              sx={navbarIconButton}
            >
              <Icon sx={iconsStyle}>{openFilters ? "close" : "filter_list"}</Icon>
            </IconButton>
          )}

          {/* YOUR ACTUAL MENU */}
          <IconButton
            size="small"
            disableRipple
            color="inherit"
            sx={navbarMobileMenu}
            onClick={handleMiniSidenav}
          >
            <Icon sx={iconsStyle} fontSize="medium">
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>

          {/* ACCOUNT */}
          <IconButton
            sx={navbarIconButton}
            size="small"
            disableRipple
            onClick={handleAccountMenuOpen}
          >
            <Icon sx={iconsStyle}>account_circle</Icon>
          </IconButton>

          {renderAccountMenu()}

          {/* NOTIFICATION */}
          <NotificationBell iconsStyle={iconsStyle} />
        </MDBox>
      </Toolbar>

      {/* =========================================================
        MOBILE / TABLET TOGGLE AREA
       ========================================================= */}
      {isMobileOrTablet && openFilters && !isMini && (
        <MDBox
          sx={{
            width: "100%",
            backgroundColor: "white",
            px: 1,
            py: 1,

            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1,

            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",

            position: "relative",
            zIndex: 0,
          }}
        >
          {/* COLLEGE ROW */}
          <MDBox
            sx={{
              width: "100%",
            }}
          >
            <TextField
              label="College"
              name="college"
              select
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                native: true,
              }}
              value={selectedCollege}
              onChange={handleCollegeChange}
            >
              <option value="">Select College</option>

              {loadingColleges && <option value="">Loading...</option>}

              {!loadingColleges &&
                colleges.map((college) => (
                  <option key={college.id} value={college.id}>
                    {college.name}
                  </option>
                ))}
            </TextField>
          </MDBox>

          {/* ROLE ROW */}
          <MDBox
            sx={{
              width: "100%",
            }}
          >
            <TextField
              label="Role"
              name="role"
              select
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                native: true,
              }}
              value={selectedRole}
              onChange={handleRoleChange}
            >
              {loadingRoles && <option value="">Loading...</option>}

              {!loadingRoles &&
                roles.map((role) => (
                  <option key={role.role_id} value={role.role_id}>
                    {role.role_name}
                  </option>
                ))}
            </TextField>
          </MDBox>

          {/* SEARCH ROW */}
          <MDBox
            sx={{
              width: "100%",
            }}
          >
            <MDInput label="Search here" fullWidth />
          </MDBox>
        </MDBox>
      )}
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
